//##############################################
//###### Segment WHY explanations ##############
/**
 * Short, readable rationale for each segment.
 * Used in modal "Benchmarks & configuration" sections.
 */
export const SEGMENT_EXPLANATIONS = {
  default: {
    attract: `
      Default ceilings reflect broad cross-retail medians.
      Select a segment for more precise engagement and conversion ceilings.
    `,
    service: `
      Default service targets follow typical retail tolerance:
      a moderate average wait and a balanced queue sensitivity.
      Select a segment to align queue settings with your format.
    `,
    flow: `
      Default capacity settings assume a standard staffed-lane utilisation band
      and conservative caps on wasted capacity and missed throughput.
      Segment-specific settings are more precise for your store type.
    `
  },

  grocery: {
    attract: `
      Physical grocery stores often convert 20–40% of visitors.
      Using aisle ceilings of 70–85% engagement and 40–60% conversion
      (implied zone CVR up to ~40%) stays within realistic top-performer
      ranges without overshooting what is seen in practice.
    `,
    service: `
      Queue research in grocery shows satisfaction and sales dropping sharply
      once waits move beyond ~4–5 minutes. That is why we use a strict
      4-minute target with high queue sensitivity and allow queue-driven loss
      of up to ~25–35% of checkout-exposed value.
    `,
    flow: `
      For capacity, grocery can safely run lanes at around 70–85% utilisation.
      Above that band, Kingman-style effects make waits explode; below it,
      you are clearly overstaffed. This is the logic behind the “healthy load”
      band and capacity loss assumptions.
    `
  },

  diy: {
    attract: `
      DIY and home-improvement formats typically see 10–20% store-level
      conversion. Aisle ceilings of 40–60% engagement and 20–30% conversion
      imply a zone CVR ceiling around 8–18%, which fits that range and
      recognises project-driven, deliberative missions.
    `,
    service: `
      Customers assembling larger project baskets tolerate slightly longer
      queues, so we use a 5–7 minute target with medium queue sensitivity
      and a 20–30% cap on queue-driven loss. This reflects a realistic trade-off
      between service and throughput.
    `,
    flow: `
      Advice-heavy interactions and variable checkout times mean that running
      lanes too “hot” creates unstable queues. A healthy utilisation band of
      roughly 65–80% helps avoid long, unpredictable queues on busy days while
      still protecting labour productivity.
    `
  },

  furniture: {
    attract: `
      Furniture, garden and consumer electronics are high-consideration
      categories where store-level conversion is usually in the low
      double digits. Ceilings of 30–50% engagement and 15–25% conversion
      imply a zone CVR ceiling of roughly 4.5–12.5%, which matches these
      deliberative buying patterns.
    `,
    service: `
      Shoppers will tolerate slightly longer waits to close big-ticket
      purchases, but poor queues still reduce attachment sales and NPS.
      We therefore use a 4–6 minute target with medium–high sensitivity
      and a 15–25% cap on queue-driven loss.
    `,
    flow: `
      Transactions often include financing, deliveries or warranty steps,
      which extend service times. To keep service quality stable and avoid
      “queue explosions”, we keep the healthy load band lower (around 60–75%)
      and treat only a moderate share of low utilisation as pure saving
      potential.
    `
  },

  fashion: {
    attract: `
      Physical fashion stores commonly convert 20–40% of visitors, far above
      e-commerce. Ceilings of 45–65% engagement and 25–35% conversion imply
      a zone CVR ceiling of about 11–23%, which is ambitious but still below
      typical best-practice store-level ranges.
    `,
    service: `
      Fashion is highly queue-sensitive: small, impulsive baskets are easily
      abandoned once waits cross ~4–5 minutes. That is why we use a 5-minute
      target with high queue sensitivity and allow queue-driven loss of
      30–40% of checkout-exposed value.
    `,
    flow: `
      To balance labour efficiency with protecting impulse and add-on sales,
      we keep healthy utilisation around 65–80%. Above ~80% utilisation,
      queue times escalate rapidly; below ~65%, checkouts are likely
      overstaffed for the actual load.
    `
  },

  discount: {
    attract: `
      Discount and value formats are high-volume and promotion-driven, often
      sitting in the lower half of the 20–40% store-conversion band. Ceilings
      of 35–60% engagement and 20–35% conversion imply a 7–21% zone CVR
      ceiling, which is realistic without overstating performance.
    `,
    service: `
      Customers in discount/value formats are very intolerant of long queues.
      Multiple studies show high abandonment when waits exceed ~5 minutes,
      so we combine a tight 3–5 minute target with very high queue sensitivity
      and allow queue-driven loss of 30–45% of checkout-exposed value.
    `,
    flow: `
      Operationally these retailers push checkout utilisation hard (up to
      ~85%) to protect labour productivity. Our capacity settings reflect that
      reality and treat a larger share of capacity-constrained value as truly
      lost rather than simply delayed, in line with highly substitutable
      baskets and low brand loyalty.
    `
  },

  pharmacy: {
    attract: `
      Pharmacy and drugstore traffic is strongly need-driven (scripts, health,
      personal care). Engagement ceilings of 50–70% and conversion ceilings of
      30–45% imply zone CVR up to ~15–31%, which is consistent with 20–40%
      store-level benchmarks while allowing for high-intent missions.
    `,
    service: `
      In a health context, queues are emotionally salient: customers become
      dissatisfied quickly beyond ~5 minutes, but some missions (e.g. urgent
      prescriptions) cannot be abandoned. We therefore use high sensitivity
      but a more conservative queue-loss cap of 20–30%.
    `,
    flow: `
      Mixed service times (consultation, scripts, payments) require extra
      headroom. A healthy utilisation band of roughly 65–80% leaves room for
      accuracy and care without creating chronic queues, and only a moderate
      share of capacity-constrained demand is treated as permanently lost.
    `
  },

  specialty: {
    attract: `
      Specialty boutiques (opticians, jewellery, premium cosmetics) attract
      highly pre-qualified visitors. Ceilings of 55–75% engagement and
      35–55% conversion imply zone CVR up to ~20–40%, still in line with
      typical top-end store ranges for physical retail.
    `,
    service: `
      These formats rely on one-to-one consultation, so customers accept
      longer waits (5–8 minutes) when the time clearly goes into service.
      Queue sensitivity is therefore medium and the max queue-loss cap more
      conservative, around 15–25% of checkout-exposed value.
    `,
    flow: `
      Checkouts are staffed by skilled roles performing mixed tasks (advice,
      fitting, after-sales). To avoid choking a time-intensive service model,
      we keep healthy utilisation in the 60–75% band and treat only a limited
      share of idle capacity as fully optimisable labour saving.
    `
  },

  large_format: {
    attract: `
      IKEA-style large-format home/furnishing concepts blend planned and
      impulse purchases. Engagement ceilings of 40–60% and conversion ceilings
      of 20–30% imply a zone CVR ceiling around 8–18%, matching global
      20–40% store-level ranges once you account for multi-visit journeys.
    `,
    service: `
      After a long store path, customers tolerate higher average waits
      (around 6–9 minutes), but CX research still shows dissatisfaction and
      lost sales when queues become extreme. We therefore set a higher target
      (7 minutes) but keep a meaningful loss curve above it with a 25–35%
      cap on queue-driven loss.
    `,
    flow: `
      Large-format stores run many lanes and self-checkout options, and can
      work efficiently at 70–85% utilisation. This range is a pragmatic
      sweet spot before Kingman effects make the system unstable; below that,
      capacity is clearly underused relative to the load.
    `
  }
}

//##############################################
//###### Gauge Store Commercial Efficiency ########
//##############################################

/**
 * Segment-level ceilings for the ATTRACT gauge.
 * Use these when you know the store's primary segment.
 *
 * Keys are segment identifiers you pass as `segmentKey`
 * elsewhere in the app (e.g. 'grocery', 'diy', 'fashion', …).
 */
// Sector-level ceilings for product zones. Source values per the
// Storalytic Benchmark Methodology document (updated 2026-06-08).
// Implied zone CVR shown in trailing comments as E × C — useful
// sanity-check against published store-level CVR ranges.
export const CEILINGS_BY_SEGMENT = {
  default:      { Emax: 0.50, Cmax: 0.55 }, // mirrors DIY (fallback)

  grocery:      { Emax: 0.72, Cmax: 0.75 }, // implied 54%
  pharmacy:     { Emax: 0.55, Cmax: 0.65 }, // implied 36%
  specialty:    { Emax: 0.65, Cmax: 0.50 }, // implied 33%
  diy:          { Emax: 0.50, Cmax: 0.55 }, // implied 28%
  discount:     { Emax: 0.45, Cmax: 0.55 }, // implied 25%
  fashion:      { Emax: 0.55, Cmax: 0.45 }, // implied 25%
  large_format: { Emax: 0.48, Cmax: 0.45 }, // implied 22%
  furniture:    { Emax: 0.45, Cmax: 0.35 }, // implied 16%
}

/**
 * Legacy / generic ceilings.
 * Keep this as the "default" pair so existing code keeps working.
 */
export const CEILINGS = CEILINGS_BY_SEGMENT.default

// Thresholds are derived from a {Emax, Cmax} ceiling pair so they can
// scale per-zone. getThresholds(ceilings) is the canonical accessor —
// classifier functions call it once per zone with that zone's resolved
// ceilings, so the same store can host (say) a DIY product aisle and a
// pharmacy-style endcap and have honest classification thresholds for
// each.
//
// Constants (MIN_VISITORS, MIN_IMPACT_EUR, HYST) are not ceiling-derived
// and stay flat across zones.
export function getThresholds(ceilings = CEILINGS) {
  const E = (ceilings && Number.isFinite(ceilings.Emax)) ? ceilings.Emax : CEILINGS.Emax
  const C = (ceilings && Number.isFinite(ceilings.Cmax)) ? ceilings.Cmax : CEILINGS.Cmax
  return {
    MIN_VISITORS: 100,                         // ignore low-signal zones

    // Scale the “low / high” cutoffs to this zone's ceilings
    LOW_ENGAGEMENT: 0.40 * E,                  // e.g. 20% when Emax=50%
    HIGH_ENGAGEMENT: 0.70 * E,                 // e.g. 35% when Emax=50%
    LOW_CONVERSION: 0.35 * C,                  // e.g. 8.75% when Cmax=25%

    // What we consider “close enough to ceiling” (for “performing well”)
    GOOD_E_HEADROOM: 0.06 * E,                 // e.g. ≤3.0 pp below Emax
    GOOD_C_HEADROOM: 0.08 * C,                 // e.g. ≤2.0 pp below Cmax

    // NOTE: the old fixed ENG_UPLIFT (+5pp) / CONV_UPLIFT (+1pp) constants
    // that drove computePotentialEUR were DELETED in the four-rung rework.
    // "Within reach" € is now the gap from current to the active benchmark
    // rung (ladder.js / LADDER below), not an arbitrary +Xpp. See
    // docs/06_four_rung_benchmark_model.md §2.4.

    // Optional business filter: don’t call it an “opportunity” if tiny
    MIN_IMPACT_EUR: 200,

    // Stabilize classification (avoid flip-flop around thresholds)
    HYST: 0.01,                                // 1 percentage point band
  }
}

// Legacy default thresholds, computed once against the default ceilings.
// Kept as a named export so existing consumers (THRESH.LOW_ENGAGEMENT
// etc.) keep working at the old default; new code should call
// getThresholds(ceilings) per zone.
export const THRESH = getThresholds(CEILINGS)

// Four-rung ladder tuning (docs/06 §2.3–2.4). MARGIN is the attainability
// hysteresis for active-rung selection (a rung must clear current by this to
// count as "above"). RANGE_PCT is the ± band on a within-reach € figure;
// RANGE_PCT_EST widens it when the winning lever is conversion, which rides
// on the manual (est.) rate. Consumed by ladder.js and mirrored into
// prioritization_config.json for ladder.py.
export const LADDER = {
  MARGIN: 0.01,
  RANGE_PCT: 0.20,
  RANGE_PCT_EST: 0.35,
}

// Labels + colors for matrix & badges.
//
// Three label variants per pattern so each surface can pick the one
// that fits its real estate:
//   - chip   : 2-3 word tag for tight spaces (table cells, dot legend)
//   - legend : longer "X gap — short why" for the legend strip
//   - plain  : one full sentence in store-manager language for
//              tooltips + Prio card subtitles. No jargon, no
//              percentages, no "ceiling" / "calibration" terms —
//              describes what a manager would say if they pointed
//              at the zone and explained what's happening.
export const PATTERN_META = {
  awareness: {
    legend: 'Engagement gap — visitors enter but don\'t stop',
    chip:   'Engagement gap',
    plain:  'Few visitors stop to engage with this zone.',
    icon:   '👁️',
    color:  '#9a6b00',
    bg:     '#fff0d0'
  },
  attention: {
    legend: 'Conversion gap — engaged visitors don\'t buy',
    chip:   'Conversion gap',
    plain:  'Visitors stop and look, but few of them buy.',
    icon:   '🧩',
    color:  '#9a2727',
    bg:     '#fff0f0'
  },
  reach: {
    legend: 'Reach gap — engages well, few find it',
    chip:   'Reach gap',
    plain:  'Engages well, but not many visitors find this zone.',
    icon:   '🧭',
    color:  '#2E8B8E',
    bg:     '#EAF7F7'
  },
  neutral: {
    legend: 'Mid-tier — solid baseline with room for incremental gains',
    chip:   'Mid-tier',
    plain:  'Performing in line with expectations — small improvements still possible.',
    icon:   '➕',
    color:  '#355b8c',
    bg:     '#eef2ff'
  },
  strong: {
    legend: 'Performing well — at or near ceiling on both engagement and conversion',
    chip:   'Performing well',
    plain:  'Performing at or near best practices for this zone.',
    icon:   '✅',
    color:  '#2f6f3a',
    bg:     '#F4FAF5'
  }
}

// ROLE_META — previously powered the "role pill" on Prio cards (the
// third pill alongside strategic + pattern chips). Pill was retired;
// the icon/text vocabulary lives on as documentation. Re-export when
// a future card revision needs to render role icons again.

export const ACTION_LIBRARY = {
  awareness: [
    { icon:'bi-signpost-2',   text:'Move nearer main aisle',        hint:'Relocate the shelf or rack closer to the main walking path.' },
    { icon:'bi-binoculars',   text:'Place hero item at eye level',  hint:'Highlight one bestseller or flagship SKU at eye height.' },
    { icon:'bi-lightning',    text:'Brighten and declutter shelf',  hint:'Improve lighting and remove visual clutter to increase visibility.' },
    { icon:'bi-megaphone',    text:'Clear price & benefit tag',     hint:'Show 1–2 clear benefits with an easy-to-spot price label.' },
    { icon:'bi-easel',        text:'Entry teaser or signpost',      hint:'Small sign near entry to pull shoppers toward the category.' },
    { icon:'bi-arrows-move',  text:'Simplify layout (max 6 SKUs)',  hint:'Less visual noise helps visitors make decisions faster.' },
    { icon:'bi-emoji-sunglasses', text:'Mini demo or open sample',  hint:'Allow touch/try experience to trigger curiosity.' },
    { icon:'bi-person-video', text:'Staff wave or quick greet',     hint:'Use light human contact to spark engagement.' },
  ],
  attention: [
    { icon:'bi-funnel',         text:'Limit choices (3–5 options)',     hint:'Simplify assortment to guide faster, confident decisions.' },
    { icon:'bi-card-checklist', text:'Show 3 key benefits',            hint:'One short card comparing main features/advantages.' },
    { icon:'bi-cash-coin',      text:'Clarify pricing tiers',          hint:'Make price-value steps obvious (Good / Better / Best).' },
    { icon:'bi-bag-check',      text:'Add “Try me” cue',               hint:'Prompt touch or sample to increase purchase intent.' },
    { icon:'bi-box-seam',       text:'Bundle with must-have item',     hint:'Create value perception and simplify add-on decisions.' },
    { icon:'bi-people',         text:'Show peer reviews or rating',    hint:'1–2 shopper quotes or star badges for social proof.' },
    { icon:'bi-hand-index-thumb', text:'“Pick this if…” guidance',     hint:'Explain which variant fits which shopper profile.' },
  ],
  reach: [
    { icon:'bi-signpost',     text:'Add sign on main aisle',          hint:'Guide traffic from the busiest lane toward the zone.' },
    { icon:'bi-arrow-right',  text:'Add floor arrow or decal',        hint:'Directional arrows help first-time visitors locate it.' },
    { icon:'bi-columns-gap',  text:'Mirror hero SKU on endcap',       hint:'Duplicate one item on a high-traffic endcap.' },
    { icon:'bi-shuffle',      text:'Shift closer to flow line',       hint:'A 1–2 m relocation often doubles visibility.' },
    { icon:'bi-link-45deg',   text:'Cross-place near related zone',   hint:'Pair complementary products together for discovery.' },
    { icon:'bi-window-plus',  text:'Add small window teaser',         hint:'Use entry window to drive awareness of this category.' },
  ],
  neutral: [
    { icon:'bi-graph-up',     text:'Test new claim or card',          hint:'A/B test simple messages to find best conversion driver.' },
    { icon:'bi-basket',       text:'Keep facings full and neat',      hint:'Well-stocked shelves signal popularity and trust.' },
    { icon:'bi-diagram-3',    text:'Create micro-bundle offer',       hint:'Suggest a 2+1 or bundle that raises basket size.' },
    { icon:'bi-stars',        text:'Refresh visuals every 4–6 weeks', hint:'Avoid “banner blindness” by renewing content regularly.' },
    { icon:'bi-clock-history',text:'Staff zone during peaks',         hint:'Be visible when dwell and traffic overlap.' },
    { icon:'bi-clipboard',    text:'Fix planogram & alignment',       hint:'Ensure clear category logic and sightlines.' },
  ],
  strong: [
    { icon:'bi-shield-check', text:'Protect stock & service',         hint:'Avoid stockouts and maintain staff support.' },
    { icon:'bi-files',        text:'Copy layout to twin zone',        hint:'Replicate this successful setup elsewhere.' },
    { icon:'bi-diagram-3',    text:'Add attach-sell trigger',         hint:'Suggest “complete the look / kit / set” buys.' },
  ],
  /* ---------- DISPLAY ZONES ---------- */
  display: [
    { icon:'bi-lightning',    text:'Add spotlights for contrast',      hint:'Focused lighting boosts stopping power.' },
    { icon:'bi-brush',        text:'Highlight one strong message',     hint:'Keep messaging simple — one visual theme, one claim.' },
    { icon:'bi-easel',        text:'Elevate key product on riser',     hint:'Use height variation to create a hero focus point.' },
    { icon:'bi-stars',        text:'Add motion or subtle animation',   hint:'Gentle movement draws eyes without feeling pushy.' },
    { icon:'bi-badge-ad',     text:'Rotate visuals every 2 weeks',    hint:'Keep displays feeling fresh with small refresh cycles.' },
    { icon:'bi-camera-video', text:'Add lifestyle visual',            hint:'Show product in use to make benefit tangible.' },
    { icon:'bi-magic',        text:'Use strong color contrast',       hint:'Ensure at least 30% contrast between background and items.' },
  ],

  /* ---------- DEMO / INTERACTION ZONES ---------- */
  demo: [
    { icon:'bi-people',       text:'Greet visitors proactively',       hint:'Smile, eye contact, and brief opener invite interaction.' },
    { icon:'bi-chat-dots',    text:'Ask a short discovery question',   hint:'“What kind of X do you use?” opens the conversation.' },
    { icon:'bi-bag-check',    text:'Offer quick hands-on test',        hint:'Let shoppers try the product themselves.' },
    { icon:'bi-trophy',       text:'Display “Best seller” or award',   hint:'Instantly signals trust and popularity.' },
    { icon:'bi-gift',         text:'Give small sample or freebie',     hint:'Create reciprocity and recall post-visit.' },
    { icon:'bi-hand-thumbs-up', text:'Show benefit in 10 s',           hint:'Clear, visual demo beats long talk.' },
    { icon:'bi-clipboard2-check', text:'Ask feedback after demo',      hint:'Simple “How was it?” creates connection and data.' },
    { icon:'bi-person-video', text:'Film short clip for display',      hint:'Looped video extends engagement between staff moments.' },
  ],
}

//##############################################


//##############################################
//###### Gauge Store Service Efficiency ########
//##############################################

/**
 * QUEUE_ELASTICITY
 *  - k_per_min: how many extra % of value we lose per minute above target
 *  - max_loss:  cap on loss fraction (0–1)
 *  - target_wait_min: target average wait in minutes
 *
 * lossFraction = min(k_per_min * excessWaitMin, max_loss)
 */
export const QUEUE_ELASTICITY = {
  // Generic fallback if no segmentKey is provided
  default: {
    k_per_min: 0.06,   // 6% per extra min above target
    max_loss: 0.35,    // up to 35% of checkout-exposed value
    target_wait_min: 5 // “typical” retail tolerance
  },

  // Grocery / FMCG: TWT ≈ 4, high sensitivity, max 25–35% loss
  grocery: {
    // hit ~30% loss at about +3 minutes above target
    k_per_min: 0.10,
    max_loss: 0.30,
    target_wait_min: 4
  },

  // DIY / Home improvement: TWT 5–7, medium sensitivity, 20–30% loss
  diy: {
    // hit ~25% loss at +5 minutes above target
    k_per_min: 0.05,
    max_loss: 0.25,
    target_wait_min: 5.5
  },

  // Furniture / Garden / CE: TWT 4–6, medium-high sensitivity, 15–25% loss
  furniture: {
    k_per_min: 0.06,   // ~24% at +4 min
    max_loss: 0.24,
    target_wait_min: 5
  },

  // Fashion / Apparel: TWT 4–6, high sensitivity, 30–40% loss
  fashion: {
    k_per_min: 0.10,   // ~35% at +3.5 min
    max_loss: 0.35,
    target_wait_min: 5
  },

  // Discount / Value: TWT 3–5, very high sensitivity, 30–45% loss
  discount: {
    k_per_min: 0.12,   // ~36% at +3 min
    max_loss: 0.40,
    target_wait_min: 4
  },

  // Pharmacy / Drugstores: TWT 3–5, high sensitivity, 20–30% loss
  pharmacy: {
    k_per_min: 0.08,   // ~24% at +3 min
    max_loss: 0.25,
    target_wait_min: 4
  },

  // Specialty boutiques: TWT 5–8, medium sensitivity, 15–25% loss
  specialty: {
    k_per_min: 0.04,   // ~20% at +5 min
    max_loss: 0.20,
    target_wait_min: 6
  },

  // IKEA-style large format: TWT 6–9, medium sensitivity, 25–35% loss
  large_format: {
    k_per_min: 0.05,   // ~25% at +5 min
    max_loss: 0.30,
    target_wait_min: 7
  }
}

export const SERVICE_BANDS = {
  CRITICAL: 40, // 0–40% → 🔴
  WARNING:  70, // 40–70% → 🟠, 70%+ → 🟢
}

//##############################################


//##############################################
//###### Gauge Store Capacity Balance ##########
//##############################################

export const CAPACITY_BANDS = {
  CRITICAL: 40, // 0–40: strong imbalance (either way)
  WARNING: 97   // 40–97: sensitive/orange, 98–100: balanced/green
  // Green only triggers in the 98–100 band (matches the gauge's
  // "balanced zone" definition — any tilt > 2% reads as
  // overstaffed/overloaded). Earlier value (70) painted gauges
  // green well before they were actually balanced.
}

// Per-segment config for wasted / missed impact (ratio side)
// (kept simple for now; you already have a diy override)
export const CAPACITY_CONFIG = {
  default: {
    lower_ratio_ok: 0.8,
    upper_ratio_ok: 1.2,
    max_overload_loss: 0.5,  // 50%
    alpha: 0.4               // realism factor for missed throughput
  },
  diy: {
    lower_ratio_ok: 0.7,
    upper_ratio_ok: 1.3,
    max_overload_loss: 0.4,
    alpha: 0.35
  }
}

/**
 * CAPACITY_VALUE_SETTINGS:
 *  - staff_cost_per_hour: fully-loaded cost of one staffed checkout hour
 *  - max_labour_saving_fraction: max share of staffed time we treat as “savings”
 *  - max_throughput_loss_fraction: max share of checkout-exposed value we treat as constrained by capacity
 *  - alpha_throughput: realism factor (0–1) for how much constrained value is real lost €
 */
export const CAPACITY_VALUE_SETTINGS = {
  // Generic fallback
  default: {
    staff_cost_per_hour: 20,
    max_labour_saving_fraction: 0.5,
    max_throughput_loss_fraction: 0.30,
    alpha_throughput: 0.60
  },

  // Grocery / FMCG
  grocery: {
    staff_cost_per_hour: 20.0,
    max_labour_saving_fraction: 0.60,
    max_throughput_loss_fraction: 0.35,
    alpha_throughput: 0.55
  },

  // DIY / Home improvement
  diy: {
    staff_cost_per_hour: 19.5,
    max_labour_saving_fraction: 0.50,
    max_throughput_loss_fraction: 0.30,
    alpha_throughput: 0.60
  },

  // Furniture / Garden / Consumer Electronics
  furniture: {
    staff_cost_per_hour: 19.5,
    max_labour_saving_fraction: 0.40,
    max_throughput_loss_fraction: 0.25,
    alpha_throughput: 0.55
  },

  // Fashion / Apparel
  fashion: {
    staff_cost_per_hour: 19.5,
    max_labour_saving_fraction: 0.50,
    max_throughput_loss_fraction: 0.40,
    alpha_throughput: 0.65
  },

  // Discount / Value
  discount: {
    staff_cost_per_hour: 19.0,
    max_labour_saving_fraction: 0.60,
    max_throughput_loss_fraction: 0.40,
    alpha_throughput: 0.65
  },

  // Pharmacy / Drugstores
  pharmacy: {
    staff_cost_per_hour: 20.0,
    max_labour_saving_fraction: 0.50,
    max_throughput_loss_fraction: 0.30,
    alpha_throughput: 0.60
  },

  // Specialty boutiques
  specialty: {
    staff_cost_per_hour: 19.5,
    max_labour_saving_fraction: 0.40,
    max_throughput_loss_fraction: 0.25,
    alpha_throughput: 0.55
  },

  // IKEA-style large format
  large_format: {
    staff_cost_per_hour: 20.0,
    max_labour_saving_fraction: 0.50,
    max_throughput_loss_fraction: 0.30,
    alpha_throughput: 0.60
  }
}

//##############################################